import React from 'react';
import { 
  GraduationCap, FileText, CheckCircle, Search, Heart, 
  Upload, Send, Clock, User, Building2, Globe, 
  TrendingUp, Bell, Settings, Edit, Eye, Award, DollarSign
} from 'lucide-react';

// Reusable slide content components
const FeatureList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
        <span className="text-gray-600">{item}</span>
      </li>
    ))}
  </ul>
);

const StepList = ({ steps }) => (
  <div className="space-y-4">
    {steps.map((step, i) => (
      <div key={i} className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-gray-800 font-semibold flex-shrink-0">
          {i + 1}
        </div>
        <div>
          <p className="font-medium text-gray-800">{step.title}</p>
          {step.description && <p className="text-sm text-gray-500 mt-1">{step.description}</p>}
        </div>
      </div>
    ))}
  </div>
);

const StatCard = ({ icon, label, value }) => (
  <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
    <div className="p-2 rounded-lg bg-teal-100">
      {icon}
    </div>
    <div>
      <p className="text-2xl font-semibold text-gray-800">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);

export const studentTrainingSlides = [
  // Slide 1: Welcome
  {
    title: "Welcome to H E Direct!",
    subtitle: "Your journey to studying abroad starts here",
    category: "Getting Started",
    icon: <GraduationCap className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-cyan-50",
    content: (
      <div className="space-y-6">
        <p className="text-lg text-gray-600">
          We're excited to help you achieve your dream of studying abroad! This guide will show you how to:
        </p>
        <div className="grid grid-cols-2 gap-4">
          <StatCard icon={<User className="w-5 h-5 text-blue-500" />} label="Build Profile" value="👤" />
          <StatCard icon={<Search className="w-5 h-5 text-green-500" />} label="Find Courses" value="🔍" />
          <StatCard icon={<FileText className="w-5 h-5 text-purple-500" />} label="Apply" value="📄" />
          <StatCard icon={<TrendingUp className="w-5 h-5 text-orange-500" />} label="Track Progress" value="📊" />
        </div>
      </div>
    ),
    tips: "Complete your profile first to get personalized course recommendations!"
  },

  // Slide 2: Complete Your Profile
  {
    title: "Build Your Profile",
    subtitle: "Tell us about yourself for better recommendations",
    category: "Getting Started",
    icon: <User className="w-8 h-8 text-blue-500" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-500/10 to-cyan-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Complete your profile to unlock personalized features:</p>
        <StepList steps={[
          { title: "Click 'Complete Profile' button", description: "On your dashboard" },
          { title: "Enter your education details", description: "Current level and field of study" },
          { title: "Set your preferences", description: "Target countries, budget, intake date" },
          { title: "Add English test scores", description: "IELTS, TOEFL, or other" },
          { title: "Save your profile", description: "Get matched courses instantly!" }
        ]} />
      </div>
    ),
    tips: "The more complete your profile, the better your course recommendations!"
  },

  // Slide 3: Explore Destinations
  {
    title: "Explore Study Destinations",
    subtitle: "9 countries, 100+ universities",
    category: "Exploration",
    icon: <Globe className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">We partner with universities across:</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { country: "🇬🇧 UK", unis: "40+" },
            { country: "🇺🇸 USA", unis: "25+" },
            { country: "🇨🇦 Canada", unis: "15+" },
            { country: "🇦🇺 Australia", unis: "12+" },
            { country: "🇮🇪 Ireland", unis: "8+" },
            { country: "🇫🇷 France", unis: "5+" },
            { country: "🇩🇪 Germany", unis: "5+" },
            { country: "🇳🇱 Netherlands", unis: "3+" },
            { country: "🇪🇸 Spain", unis: "3+" },
          ].map((item, i) => (
            <div key={i} className="bg-gray-100 rounded-xl p-3 text-center">
              <p className="text-lg">{item.country}</p>
              <p className="text-xs text-gray-500">{item.unis} universities</p>
            </div>
          ))}
        </div>
      </div>
    ),
    tips: "Click 'Destinations' in the menu to compare countries side-by-side!"
  },

  // Slide 4: Search Courses
  {
    title: "Finding the Right Course",
    subtitle: "Search and filter 688+ courses",
    category: "Course Search",
    icon: <Search className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Use filters to find your perfect course:</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-100 rounded-xl p-4">
            <p className="font-medium text-gray-800 mb-3">🔍 Filter By</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Country</li>
              <li>• Field of Study</li>
              <li>• Degree Level</li>
              <li>• Budget Range</li>
            </ul>
          </div>
          <div className="bg-gray-100 rounded-xl p-4">
            <p className="font-medium text-gray-800 mb-3">📋 Course Details</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Tuition fees</li>
              <li>• Duration</li>
              <li>• IELTS requirement</li>
              <li>• Intake dates</li>
            </ul>
          </div>
        </div>
      </div>
    ),
    tips: "Use 'Check Eligibility' to see courses matching your profile!"
  },

  // Slide 5: Wishlist
  {
    title: "Save to Wishlist",
    subtitle: "Bookmark courses you're interested in",
    category: "Course Search",
    icon: <Heart className="w-8 h-8 text-red-500" />,
    iconBg: "bg-red-100",
    bgColor: "bg-gradient-to-r from-red-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Found an interesting course? Save it for later:</p>
        <StepList steps={[
          { title: "Browse courses", description: "Search or view recommendations" },
          { title: "Click the heart icon ❤️", description: "On any course card" },
          { title: "View your Wishlist", description: "In 'My Dashboard' → 'Wishlist' tab" },
          { title: "Compare and decide", description: "Review all saved courses together" }
        ]} />
        <div className="mt-4 p-3 bg-pink-500/10 rounded-xl border border-pink-500/30">
          <p className="text-sm text-pink-600">💕 No limit on wishlist items - save as many as you like!</p>
        </div>
      </div>
    ),
    tips: "Compare wishlist courses side-by-side to make the best decision."
  },

  // Slide 6: Applying
  {
    title: "Submitting Applications",
    subtitle: "Apply to your chosen courses",
    category: "Applications",
    icon: <Send className="w-8 h-8 text-cyan-500" />,
    iconBg: "bg-cyan-100",
    bgColor: "bg-gradient-to-r from-cyan-500/10 to-blue-500/10",
    content: (
      <div className="space-y-6">
        <StepList steps={[
          { title: "Find your course", description: "Search or pick from wishlist" },
          { title: "Click 'Apply Now'", description: "On the course card" },
          { title: "Upload required documents", description: "Passport, transcripts, test scores" },
          { title: "Submit application", description: "We'll process and forward to university" }
        ]} />
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-xl p-3 text-center">
            <p className="text-teal-600 font-semibold">Up to 3 Apps</p>
            <p className="text-xs text-gray-500">Apply to up to 3 courses</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-3 text-center">
            <p className="text-green-500 font-semibold">Free Support</p>
            <p className="text-xs text-gray-500">We guide you throughout</p>
          </div>
        </div>
        <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm text-amber-700">⚠️ Note: You can apply to a maximum of 3 courses. Contact admin if you need more.</p>
        </div>
      </div>
    ),
    tips: "Apply to 2-3 courses to maximize your chances of acceptance!"
  },

  // Slide 7: Document Upload
  {
    title: "Uploading Documents",
    subtitle: "What you need for your application",
    category: "Applications",
    icon: <Upload className="w-8 h-8 text-orange-500" />,
    iconBg: "bg-orange-100",
    bgColor: "bg-gradient-to-r from-orange-500/10 to-amber-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Common documents required:</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: "🛂", name: "Passport", desc: "Valid for 6+ months" },
            { icon: "📜", name: "Transcripts", desc: "Academic records" },
            { icon: "📝", name: "English Test", desc: "IELTS/TOEFL/PTE" },
            { icon: "📄", name: "CV/Resume", desc: "For postgrad" },
            { icon: "✉️", name: "Statement", desc: "Personal statement" },
            { icon: "📋", name: "References", desc: "Academic/professional" },
          ].map((doc, i) => (
            <div key={i} className="flex items-center gap-3 bg-gray-100 rounded-xl p-3">
              <span className="text-2xl">{doc.icon}</span>
              <div>
                <p className="font-medium text-gray-800">{doc.name}</p>
                <p className="text-xs text-gray-500">{doc.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
    tips: "Scan documents clearly in PDF format for best results."
  },

  // Slide 8: Journey Tracking
  {
    title: "Track Your Journey",
    subtitle: "Follow your application through 8 stages",
    category: "Progress",
    icon: <TrendingUp className="w-8 h-8 text-indigo-500" />,
    iconBg: "bg-indigo-500/20",
    bgColor: "bg-gradient-to-r from-indigo-500/10 to-violet-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Your application journey has 8 milestones:</p>
        <div className="grid grid-cols-4 gap-2 text-xs">
          {[
            "1. Submitted", "2. Offer Issued", "3. Offer Accepted", "4. UCO",
            "5. Deposit", "6. CAS/Visa", "7. Visa Approved", "8. Enrolled"
          ].map((stage, i) => (
            <div key={i} className={`p-2 rounded-lg text-center ${i < 2 ? 'bg-blue-100 text-blue-600' : i < 5 ? 'bg-teal-100 text-teal-600' : 'bg-green-100 text-green-600'}`}>
              {stage}
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-4">
          ✅ Green = Completed | 🟡 Yellow = Current | ⚪ Grey = Upcoming
        </p>
      </div>
    ),
    tips: "You'll receive email notifications when your application progresses!"
  },

  // Slide 9: Getting Help
  {
    title: "Getting Help",
    subtitle: "We're here for you every step",
    category: "Support",
    icon: <Bell className="w-8 h-8 text-pink-500" />,
    iconBg: "bg-pink-100",
    bgColor: "bg-gradient-to-r from-pink-500/10 to-rose-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Multiple ways to get assistance:</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-100 rounded-xl p-4 text-center">
            <span className="text-3xl">💬</span>
            <p className="font-medium text-gray-800 mt-2">WhatsApp Chat</p>
            <p className="text-sm text-gray-500">Click the green button</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 text-center">
            <span className="text-3xl">📧</span>
            <p className="font-medium text-gray-800 mt-2">Email Support</p>
            <p className="text-sm text-gray-500">admin.team@hedirect.org</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 text-center">
            <span className="text-3xl">📞</span>
            <p className="font-medium text-gray-800 mt-2">Phone</p>
            <p className="text-sm text-gray-500">+92 326 8096371</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 text-center">
            <span className="text-3xl">❓</span>
            <p className="font-medium text-gray-800 mt-2">Help Page</p>
            <p className="text-sm text-gray-500">FAQs & guides</p>
          </div>
        </div>
      </div>
    ),
    tips: "WhatsApp is fastest for quick questions during office hours!"
  },

  // Slide 10: Scholarships
  {
    title: "Find Scholarships",
    subtitle: "Get funding for your studies",
    category: "Scholarships",
    icon: <Award className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-yellow-500/10 to-amber-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Explore funding opportunities from your dashboard:</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-green-500">
            <DollarSign className="w-6 h-6 text-green-500 mb-2" />
            <p className="font-medium text-gray-800">Fully Funded</p>
            <p className="text-sm text-gray-500">Chevening, Commonwealth, Rhodes, Gates Cambridge</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-yellow-500">
            <Award className="w-6 h-6 text-teal-600 mb-2" />
            <p className="font-medium text-gray-800">Partial Funding</p>
            <p className="text-sm text-gray-500">GREAT Scholarships, DAAD, university-specific</p>
          </div>
        </div>
        <StepList steps={[
          { title: "Click 'Explore Scholarships'", description: "Find the banner on your dashboard" },
          { title: "Search & Filter", description: "Filter by country, degree level, funding type" },
          { title: "View Details", description: "Check eligibility, benefits, requirements" },
          { title: "Apply via H E Direct", description: "Click Apply - our team will guide you through the process!" }
        ]} />
      </div>
    ),
    tips: "Popular scholarships: Chevening (UK), Fulbright (USA), DAAD (Germany), Erasmus (Europe)"
  },

  // Slide 11: Student ID Card
  {
    title: "Your Student ID Card",
    subtitle: "Access your digital ID anytime",
    category: "New Feature",
    icon: <Award className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Access your H E Direct Student ID from your dashboard:</p>
        <StepList steps={[
          { title: "Go to Dashboard", description: "Login to your student account" },
          { title: "Click 'My ID Card'", description: "Button at the top of your dashboard" },
          { title: "View Your ID", description: "Shows your name, email, student ID, and member since date" },
          { title: "Scan QR Code", description: "Install the app on your phone for easy access" }
        ]} />
        <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
          <p className="text-sm text-purple-700">💡 The QR code lets you install the H E Direct app on your phone for quick access!</p>
        </div>
      </div>
    ),
    tips: "Keep your Student ID handy - you may need it for verification!"
  },

  // Slide 12: Summary
  {
    title: "Ready to Start!",
    subtitle: "Your study abroad journey begins now",
    category: "Summary",
    icon: <CheckCircle className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Your next steps:</p>
        <FeatureList items={[
          "Complete your profile (get recommendations!)",
          "Explore destinations and universities",
          "Search courses and save to wishlist",
          "Browse scholarships for funding",
          "Apply to your top choices (max 3)",
          "Upload all required documents",
          "Track your journey to enrollment!",
          "Get your Student ID Card"
        ]} />
        <div className="mt-6 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-200">
          <p className="text-teal-600 font-medium">📞 Need Help?</p>
          <p className="text-gray-600 text-sm mt-1">WhatsApp: +92 326 8096371 | Email: admin.team@hedirect.org</p>
        </div>
      </div>
    ),
    tips: "You can restart this training anytime from the Training Hub!"
  },

  // === NEW FEATURES (2026) ===
  // Slide: Public Visa Probability Checker
  {
    title: "Try Our Visa Probability Checker",
    subtitle: "Free AI prediction before you apply",
    category: "New • AI",
    icon: <Award className="w-8 h-8 text-indigo-600" />,
    iconBg: "bg-indigo-100",
    bgColor: "bg-gradient-to-r from-indigo-50 to-violet-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Worried about your visa odds? Use our free, AI-powered Visa Probability
          Checker before committing to an application.
        </p>
        <FeatureList items={[
          "Visit /visa-probability on the website",
          "Enter destination, academic profile, finances, English score",
          "Get an approval probability + risk factors in 30 seconds",
          "Save the report or share it with your counsellor"
        ]} />
        <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
          <p className="text-sm text-indigo-700"><strong>Note:</strong> The score is advisory only — final outcomes depend on the embassy.</p>
        </div>
      </div>
    ),
    tips: "Use this once you have your IELTS score — it improves accuracy."
  },

  // Slide: Smart Matching for Students
  {
    title: "We Match Courses to You",
    subtitle: "No more endless scrolling",
    category: "Enhanced",
    icon: <TrendingUp className="w-8 h-8 text-emerald-600" />,
    iconBg: "bg-emerald-100",
    bgColor: "bg-gradient-to-r from-emerald-50 to-teal-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          The more details you add to your profile, the smarter your course recommendations
          become. We factor in all your academic percentages and any English test.
        </p>
        <FeatureList items={[
          "Fill Matric, Inter & Bachelors marks in your profile",
          "Add any English test score (IELTS / PTE / Duolingo / TOEFL)",
          "Top 3 matches appear with green 'Best Fit' badges",
          "Recommendations refresh as you update your profile"
        ]} />
      </div>
    ),
    tips: "A complete profile gets you 3× better matches. It's worth 5 minutes."
  },

  // Slide: Documents Required Checklist
  {
    title: "Your Documents Checklist",
    subtitle: "Know exactly what to upload",
    category: "New",
    icon: <CheckCircle className="w-8 h-8 text-green-600" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-50 to-emerald-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Each application now shows a "Documents Required" banner listing every file
          you need — colour-coded by status.
        </p>
        <FeatureList items={[
          "Missing — please upload",
          "Pending review — admin is checking",
          "Approved — you're good",
          "Rejected — re-upload with the noted fix"
        ]} />
        <div className="p-4 bg-green-50 rounded-xl border border-green-200">
          <p className="text-sm text-green-700"><strong>Pro tip:</strong> Clear all missing items before the application deadline.</p>
        </div>
      </div>
    ),
    tips: "Files are stored securely. They're never deleted unless you ask."
  },

  // Slide: Visa Outcome Tracking
  {
    title: "Track Your Visa Outcome",
    subtitle: "Every step recorded in your journey",
    category: "Transparency",
    icon: <Globe className="w-8 h-8 text-blue-600" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-50 to-cyan-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Your application Journey now includes a Visa Outcome stage — see status
          updates as your counsellor records each milestone.
        </p>
        <FeatureList items={[
          "Application Submitted → Offer → CAS/I-20 → Visa Applied → Visa Outcome",
          "Each stage timestamped automatically",
          "Approved / Refused outcomes logged with reasons",
          "Refused cases trigger an automatic advisory call from your counsellor"
        ]} />
      </div>
    ),
    tips: "Open your Application page weekly to stay on top of updates."
  }
];

export default studentTrainingSlides;
